import { BaseFeature } from '../../core/base-feature.js';

/**
 * ConfigManagerFeature - Frontend configuration management
 * Communicates with desktop ConfigManager via data channels
 * Provides real-time config synchronization and updates
 */
export class ConfigManagerFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.configData = new Map();
        this.configSchemas = new Map();
        this.changeListeners = new Set();
        this.isConnected = false;
        this.pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'config-manager',
            name: 'config-manager',
            version: 'v1.0',
            capabilities: [
                'get_config',
                'set_config',
                'update_config',
                'delete_config',
                'get_config_tree',
                'validate_config',
                'export_config',
                'import_config',
                'subscribe_to_changes',
                'unsubscribe_from_changes'
            ],
            dependencies: []
        };
    }

    async initialize() {
        this.log('Initializing ConfigManager feature...');
        // Frontend config-manager is passive - it responds to requests
        // No need to set up listeners, messages are automatically routed to handleMessage()
        this.log('✓ ConfigManager feature initialized');
    }

    /**
     * Called when remote capabilities are received
     */
    onRemoteCapabilities(capabilities) {
        const configManagerCapability = capabilities.config_manager;
        if (configManagerCapability) {
            this.isConnected = true;
            this.log('✓ Connected to desktop ConfigManager');
            // Optionally request initial configuration
            // this.requestFullConfig();
        } else {
            this.log('Desktop does not support ConfigManager', 'warn');
            this.isConnected = false;
        }
    }

    /**
     * Request full configuration from desktop
     */
    async requestFullConfig() {
        try {
            const response = await this.sendMessage('get_config_tree', {});
            if (response.success) {
                this.updateLocalConfig(response.config);
                this.log('✓ Received full configuration from desktop');
            } else {
                throw new Error(response.error || 'Failed to get configuration');
            }
        } catch (error) {
            this.log(`Error requesting full config: ${error.message}`, 'error');
        }
    }

    /**
     * Update local configuration data
     */
    updateLocalConfig(config) {
        this.configData = this.objectToMap(config);
        this.log(`Updated local config with ${this.configData.size} entries`);
    }

    /**
     * Update a specific configuration value
     */
    updateConfigValue(path, value) {
        const keys = path.split('.');
        let current = this.configData;

        // Navigate to the parent of the target key
        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!(current instanceof Map)) {
                this.log(`Cannot update config: ${keys.slice(0, i + 1).join('.')} is not an object`, 'error');
                return false;
            }

            if (!current.has(key) || !(current.get(key) instanceof Map)) {
                current.set(key, new Map());
            }
            current = current.get(key);
        }

        const finalKey = keys[keys.length - 1];
        current.set(finalKey, value);

        this.log(`Updated config value: ${path} = ${JSON.stringify(value)}`);
        return true;
    }

    /**
     * Convert nested object to Map structure
     */
    objectToMap(obj) {
        const result = new Map();

        for (const [key, value] of Object.entries(obj)) {
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                result.set(key, this.objectToMap(value));
            } else {
                result.set(key, value);
            }
        }

        return result;
    }

    /**
     * Convert Map structure to nested object
     */
    mapToObject(map) {
        const result = {};

        for (const [key, value] of map.entries()) {
            if (value instanceof Map) {
                result[key] = this.mapToObject(value);
            } else {
                result[key] = value;
            }
        }

        return result;
    }

    /**
     * Get a configuration value by path
     * @param {string} path - Dot notation path (e.g., 'features.chat.enabled')
     * @returns {any} Configuration value or undefined
     */
    getConfig(path) {
        if (!this.isConnected) {
            this.log('Not connected to desktop ConfigManager', 'warn');
            return undefined;
        }

        const keys = path.split('.');
        let current = this.configData;

        for (const key of keys) {
            if (current instanceof Map && current.has(key)) {
                current = current.get(key);
            } else {
                return undefined;
            }
        }

        return current;
    }

    /**
     * Set a configuration value by path
     * @param {string} path - Dot notation path
     * @param {any} value - Value to set
     * @returns {Promise<boolean>} Success status
     */
    async setConfig(path, value) {
        if (!this.isConnected) {
            throw new Error('Not connected to desktop ConfigManager');
        }

        try {
            const response = await this.sendMessage('set_config', {
                path: path,
                value: value
            });

            if (response.success) {
                // Update local config immediately for responsive UI
                this.updateConfigValue(path, value);
                return true;
            } else {
                throw new Error(response.error || 'Failed to set configuration');
            }
        } catch (error) {
            this.log(`Error setting config ${path}: ${error.message}`, 'error');
            throw error;
        }
    }

    /**
     * Update multiple configuration values at once
     * @param {Object} updates - Object with path-value pairs
     * @returns {Promise<Object>} Update results
     */
    async updateConfig(updates) {
        if (!this.isConnected) {
            throw new Error('Not connected to desktop ConfigManager');
        }

        try {
            const response = await this.sendMessage('update_config', {
                updates: updates
            });

            if (response.success) {
                // Update local config for responsive UI
                for (const [path, value] of Object.entries(updates)) {
                    this.updateConfigValue(path, value);
                }
                return response.results;
            } else {
                throw new Error(response.error || 'Failed to update configuration');
            }
        } catch (error) {
            this.log(`Error updating config: ${error.message}`, 'error');
            throw error;
        }
    }

    /**
     * Delete a configuration value
     * @param {string} path - Dot notation path
     * @returns {Promise<boolean>} Success status
     */
    async deleteConfig(path) {
        if (!this.isConnected) {
            throw new Error('Not connected to desktop ConfigManager');
        }

        try {
            const response = await this.sendMessage('delete_config', {
                path: path
            });

            if (response.success) {
                // Update local config
                const keys = path.split('.');
                let current = this.configData;

                // Navigate to the parent of the target key
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    if (current instanceof Map && current.has(key)) {
                        current = current.get(key);
                    } else {
                        return false;
                    }
                }

                const finalKey = keys[keys.length - 1];
                current.delete(finalKey);

                return true;
            } else {
                throw new Error(response.error || 'Failed to delete configuration');
            }
        } catch (error) {
            this.log(`Error deleting config ${path}: ${error.message}`, 'error');
            throw error;
        }
    }

    /**
     * Get the entire configuration as a tree structure
     * @returns {Object} Configuration tree
     */
    getConfigTree() {
        return this.mapToObject(this.configData);
    }

    /**
     * Validate a configuration value against schema
     * @param {string} path - Config path
     * @param {any} value - Value to validate
     * @returns {Promise<Object>} Validation result
     */
    async validateConfig(path, value) {
        if (!this.isConnected) {
            throw new Error('Not connected to desktop ConfigManager');
        }

        try {
            const response = await this.sendMessage('validate_config', {
                path: path,
                value: value
            });

            if (response.success) {
                return {
                    valid: response.valid,
                    message: response.message
                };
            } else {
                throw new Error(response.error || 'Validation failed');
            }
        } catch (error) {
            this.log(`Error validating config ${path}: ${error.message}`, 'error');
            throw error;
        }
    }

    /**
     * Export configuration to a file
     * @param {string} format - Export format ('json', 'yaml')
     * @returns {Promise<string>} Exported configuration data
     */
    async exportConfig(format = 'json') {
        if (!this.isConnected) {
            throw new Error('Not connected to desktop ConfigManager');
        }

        try {
            const response = await this.sendMessage('export_config', {
                format: format
            });

            if (response.success) {
                return response.data;
            } else {
                throw new Error(response.error || 'Export failed');
            }
        } catch (error) {
            this.log(`Error exporting config: ${error.message}`, 'error');
            throw error;
        }
    }

    /**
     * Import configuration from data
     * @param {string} configData - Configuration data as string
     * @param {string} format - Import format ('json', 'yaml')
     * @returns {Promise<Object>} Import result
     */
    async importConfig(configData, format = 'json') {
        if (!this.isConnected) {
            throw new Error('Not connected to desktop ConfigManager');
        }

        try {
            const response = await this.sendMessage('import_config', {
                data: configData,
                format: format
            });

            if (response.success) {
                // Update local config with imported data
                this.updateLocalConfig(JSON.parse(configData));
                return response.result;
            } else {
                throw new Error(response.error || 'Import failed');
            }
        } catch (error) {
            this.log(`Error importing config: ${error.message}`, 'error');
            throw error;
        }
    }

    /**
     * Subscribe to configuration changes
     * @param {Function} callback - Callback function
     * @returns {string} Subscription ID
     */
    subscribeToChanges(callback) {
        const subscriptionId = Math.random().toString(36).substring(2, 15);
        this.changeListeners.add(callback);
        return subscriptionId;
    }

    /**
     * Unsubscribe from configuration changes
     * @param {string} subscriptionId - Subscription ID
     */
    unsubscribeFromChanges(subscriptionId) {
        // For simplicity, we'll use the callback function as the identifier
        // In a real implementation, you'd want a more sophisticated system
        this.changeListeners.clear();
    }

    /**
     * Notify all change listeners
     * @param {Object} changeEvent - Change event data
     */
    notifyChangeListeners(changeEvent) {
        this.changeListeners.forEach(callback => {
            try {
                callback(changeEvent);
            } catch (error) {
                this.log(`Error in change listener: ${error.message}`, 'error');
            }
        });
    }

    /**
     * Handle incoming messages for this feature
     */
    async handleMessage(message) {
        const { action, payload } = message;

        this.log(`Handling action: ${action}`);

        try {
            switch (action) {
                case 'config_updated':
                    // Handle config updates from desktop
                    this.updateLocalConfig(payload.config);
                    this.notifyChangeListeners({
                        type: 'full_update',
                        config: payload.config,
                        timestamp: payload.timestamp
                    });
                    return { success: true };

                case 'config_change':
                    // Handle individual config changes from desktop
                    this.updateConfigValue(payload.path, payload.newValue);
                    this.notifyChangeListeners({
                        type: 'value_change',
                        path: payload.path,
                        oldValue: payload.oldValue,
                        newValue: payload.newValue,
                        timestamp: payload.timestamp
                    });
                    return { success: true };

                case 'get_config':
                    return {
                        success: true,
                        value: this.getConfig(payload.path)
                    };

                case 'set_config':
                    return {
                        success: await this.setConfig(payload.path, payload.value),
                        message: 'Config updated'
                    };

                case 'update_config':
                    const updateResults = await this.updateConfig(payload.updates);
                    return {
                        success: true,
                        results: updateResults
                    };

                case 'delete_config':
                    return {
                        success: await this.deleteConfig(payload.path),
                        message: 'Config deleted'
                    };

                case 'get_config_tree':
                    return {
                        success: true,
                        config: this.getConfigTree()
                    };

                case 'validate_config':
                    const validation = await this.validateConfig(payload.path, payload.value);
                    return {
                        success: true,
                        valid: validation.valid,
                        message: validation.message
                    };

                case 'export_config':
                    const exportedData = await this.exportConfig(payload.format);
                    return {
                        success: true,
                        data: exportedData,
                        format: payload.format
                    };

                case 'import_config':
                    const importResult = await this.importConfig(payload.data, payload.format);
                    return {
                        success: true,
                        result: importResult
                    };

                case 'subscribe_to_changes':
                    const subscriptionId = this.subscribeToChanges(payload.callback);
                    return {
                        success: true,
                        subscriptionId
                    };

                case 'unsubscribe_from_changes':
                    this.unsubscribeFromChanges(payload.subscriptionId);
                    return {
                        success: true,
                        message: 'Unsubscribed from config changes'
                    };

                default:
                    throw new Error(`Unknown action: ${action}`);
            }
        } catch (error) {
            this.log(`Error handling ${action}: ${error.message}`, 'error');
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get connection status
     * @returns {boolean} Connection status
     */
    isConfigManagerConnected() {
        return this.isConnected;
    }

    /**
     * Log a message with feature prefix
     * @param {string} message - Message to log
     * @param {string} level - Log level ('log', 'warn', 'error')
     */
    log(message, level = 'log') {
        const prefix = `[${this.constructor.getMetadata().name}]`;
        const formattedMessage = `${prefix} ${message}`;

        switch (level) {
            case 'warn':
                console.warn(formattedMessage);
                break;
            case 'error':
                console.error(formattedMessage);
                break;
            default:
                console.log(formattedMessage);
        }
    }
}
